import React, { useState, useMemo } from 'react';
import { useApp } from '../context/AppContext';
import { Calendar, Clock, Send, X, AlertTriangle, Check, XCircle, Loader, History } from 'lucide-react';
import { format, parseISO, differenceInDays, isPast } from 'date-fns';
import Modal from './Modal';
import './TimeOffRequest.css';

function TimeOffRequest({ isOpen, onClose, employeeId }) {
  const { addTimeOffRequest, getEmployeeTimeOff, cancelTimeOffRequest, state } = useApp();
  
  const [activeTab, setActiveTab] = useState('new');
  const [formData, setFormData] = useState({
    startDate: '',
    endDate: '',
    type: 'pto',
    reason: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [cancelling, setCancelling] = useState(null);
  const [conflicts, setConflicts] = useState([]);

  const timeOffTypes = [
    { value: 'pto', label: 'Paid Time Off (PTO)' },
    { value: 'sick', label: 'Sick Leave' },
    { value: 'personal', label: 'Personal Day' },
    { value: 'unpaid', label: 'Unpaid Leave' }
  ];

  const getTypeLabel = (type) => {
    const found = timeOffTypes.find(t => t.value === type);
    return found ? found.label : type;
  };

  // Get all requests for this employee, sorted newest first
  const myRequests = useMemo(() => {
    if (!employeeId) return [];
    return getEmployeeTimeOff(employeeId)
      .filter(r => r.status !== 'cancelled')
      .sort((a, b) => new Date(b.requestedAt || b.createdAt) - new Date(a.requestedAt || a.createdAt));
  }, [employeeId, state.timeOffRequests]);

  const pendingCount = myRequests.filter(r => r.status === 'pending').length;
  const upcomingApproved = myRequests.filter(r => r.status === 'approved' && !isPast(parseISO(r.endDate)));

  const checkConflicts = (startDate, endDate) => {
    if (!startDate) return;
    const end = endDate || startDate;
    const foundConflicts = [];
    let currentDate = parseISO(startDate);
    const lastDate = parseISO(end);
    
    while (currentDate <= lastDate) {
      const dateStr = format(currentDate, 'yyyy-MM-dd');
      const dayJobs = state.jobs.filter(j => 
        j.date === dateStr && 
        (j.assignedTeam?.includes(employeeId) || j.team?.includes(employeeId))
      );
      if (dayJobs.length > 0) {
        foundConflicts.push({ date: dateStr, jobCount: dayJobs.length });
      }
      currentDate = new Date(currentDate.getTime() + 24 * 60 * 60 * 1000);
    }
    setConflicts(foundConflicts);
  };

  const handleDateChange = (field, value) => {
    const newData = { ...formData, [field]: value };
    setFormData(newData);
    if (field === 'startDate' || field === 'endDate') {
      checkConflicts(
        field === 'startDate' ? value : formData.startDate,
        field === 'endDate' ? value : formData.endDate
      );
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.startDate) {
      alert('Please select a start date');
      return;
    }
    setSubmitting(true);
    try {
      await addTimeOffRequest({
        employeeId,
        startDate: formData.startDate,
        endDate: formData.endDate || formData.startDate,
        type: formData.type,
        reason: formData.reason
      });
      setFormData({ startDate: '', endDate: '', type: 'pto', reason: '' });
      setConflicts([]);
      setActiveTab('history');
    } catch (error) {
      console.error('Error submitting time off request:', error);
      alert('Failed to submit request. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = async (requestId) => {
    if (!window.confirm('Cancel this time off request?')) return;
    setCancelling(requestId);
    try {
      await cancelTimeOffRequest(requestId);
    } catch (error) {
      console.error('Error cancelling request:', error);
      alert('Failed to cancel. Please try again.');
    } finally {
      setCancelling(null);
    }
  };

  const getDaysCount = () => {
    if (!formData.startDate) return 0;
    const end = formData.endDate || formData.startDate;
    return differenceInDays(parseISO(end), parseISO(formData.startDate)) + 1;
  };

  const getStatusInfo = (status) => {
    switch (status) {
      case 'pending': return { label: 'Pending', className: 'status-pending', icon: <Clock size={14} /> };
      case 'approved': return { label: 'Approved', className: 'status-approved', icon: <Check size={14} /> };
      case 'denied': return { label: 'Denied', className: 'status-denied', icon: <XCircle size={14} /> };
      default: return { label: status, className: '', icon: null };
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Time Off">
      <div className="time-off-tabs">
        <button 
          className={`tab-btn ${activeTab === 'new' ? 'active' : ''}`}
          onClick={() => setActiveTab('new')}
        >
          <Send size={16} /> New Request
        </button>
        <button 
          className={`tab-btn ${activeTab === 'history' ? 'active' : ''}`}
          onClick={() => setActiveTab('history')}
        >
          <History size={16} /> My Requests
          {pendingCount > 0 && <span className="tab-badge">{pendingCount}</span>}
        </button>
      </div>

      {activeTab === 'new' ? (
        <form onSubmit={handleSubmit} className="time-off-form">
          <div className="form-row">
            <div className="form-group">
              <label><Calendar size={16} /> Start Date</label>
              <input
                type="date"
                value={formData.startDate}
                onChange={(e) => handleDateChange('startDate', e.target.value)}
                min={format(new Date(), 'yyyy-MM-dd')}
                required
              />
            </div>
            <div className="form-group">
              <label><Calendar size={16} /> End Date</label>
              <input
                type="date"
                value={formData.endDate}
                onChange={(e) => handleDateChange('endDate', e.target.value)}
                min={formData.startDate || format(new Date(), 'yyyy-MM-dd')}
              />
              <span className="form-hint">Leave blank for single day</span>
            </div>
          </div>

          {formData.startDate && (
            <div className="days-summary">
              <Clock size={16} />
              <span>{getDaysCount()} day{getDaysCount() !== 1 ? 's' : ''} requested</span>
            </div>
          )}

          {conflicts.length > 0 && (
            <div className="conflict-warning">
              <AlertTriangle size={18} />
              <div>
                <strong>Schedule Conflict</strong>
                <p>You have jobs scheduled on {conflicts.length} of these days:</p>
                <ul>
                  {conflicts.map(c => (
                    <li key={c.date}>
                      {format(parseISO(c.date), 'EEE, MMM d')} - {c.jobCount} job{c.jobCount > 1 ? 's' : ''}
                    </li>
                  ))}
                </ul>
                <p>Your request will still be submitted for manager review.</p>
              </div>
            </div>
          )}

          <div className="form-group">
            <label>Type of Leave</label>
            <select
              value={formData.type}
              onChange={(e) => setFormData({ ...formData, type: e.target.value })}
            >
              {timeOffTypes.map(type => (
                <option key={type.value} value={type.value}>{type.label}</option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label>Reason (optional)</label>
            <textarea
              value={formData.reason}
              onChange={(e) => setFormData({ ...formData, reason: e.target.value })}
              placeholder="Add any notes for your manager..."
              rows={3}
            />
          </div>

          <div className="form-actions">
            <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={submitting}>
              {submitting ? 'Submitting...' : <><Send size={16} /> Submit Request</>}
            </button>
          </div>
        </form>
      ) : (
        <div className="time-off-history">
          {upcomingApproved.length > 0 && (
            <div className="upcoming-banner">
              <Check size={16} />
              <span>{upcomingApproved.length} upcoming approved day{upcomingApproved.length !== 1 ? 's' : ''} off</span>
            </div>
          )}

          {myRequests.length === 0 ? (
            <div className="empty-history">
              <Calendar size={40} />
              <p>No time off requests yet</p>
              <button className="btn btn-primary" onClick={() => setActiveTab('new')}>
                <Send size={16} /> Request Time Off
              </button>
            </div>
          ) : (
            <div className="history-list">
              {myRequests.map(request => {
                const statusInfo = getStatusInfo(request.status);
                const isExpired = isPast(parseISO(request.endDate));
                const canCancel = request.status === 'pending';
                const dayCount = differenceInDays(parseISO(request.endDate), parseISO(request.startDate)) + 1;
                
                return (
                  <div key={request.id} className={`history-card ${isExpired ? 'expired' : ''} ${request.status}`}>
                    <div className="history-card-top">
                      <div className="history-dates">
                        <Calendar size={15} />
                        <span>
                          {format(parseISO(request.startDate), 'MMM d')}
                          {request.endDate !== request.startDate && (
                            <> – {format(parseISO(request.endDate), 'MMM d')}</>
                          )}
                          {', '}{format(parseISO(request.startDate), 'yyyy')}
                        </span>
                        <span className="day-count">{dayCount}d</span>
                      </div>
                      <span className={`status-pill ${statusInfo.className}`}>
                        {statusInfo.icon} {statusInfo.label}
                      </span>
                    </div>

                    <div className="history-type">{getTypeLabel(request.type)}</div>

                    {request.reason && (
                      <div className="history-reason">"{request.reason}"</div>
                    )}

                    {request.managerNote && (
                      <div className="history-manager-note">
                        <strong>Manager response:</strong> {request.managerNote}
                      </div>
                    )}

                    <div className="history-meta">
                      Submitted {format(parseISO(request.requestedAt || request.createdAt), 'MMM d, yyyy')}
                      {request.reviewedAt && (
                        <> · Reviewed {format(parseISO(request.reviewedAt), 'MMM d')}</>
                      )}
                    </div>

                    {canCancel && (
                      <button 
                        className="cancel-request-btn"
                        onClick={() => handleCancel(request.id)}
                        disabled={cancelling === request.id}
                      >
                        {cancelling === request.id ? (
                          <><Loader size={14} className="spin" /> Cancelling...</>
                        ) : (
                          <><X size={14} /> Cancel Request</>
                        )}
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </Modal>
  );
}

export default TimeOffRequest;
